import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Table, Tooltip, message } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getClientInvoiceFieldChangesV2 } from 'store/clientInvoiceSliceV2';
import { getSignedUrl } from 'store/commonSlice';

const CLIENT_INVOICE_ENTITY = 'client_invoice';

const formatCurrency = (value) =>
	value == null ? '-' : `₹${(Number(value) || 0).toLocaleString('en-IN')}`;
const formatDateTime = (v) => (v ? moment(v).format('DD-MM-YYYY h:mm A') : '-');

const STATUS_COLOR = {
	pending: '#e08a2b',
	accepted: '#3f9c53',
	approved: '#3f9c53',
	rejected: '#d84a4a',
};

/**
 * Edit History table for the client invoice detail page (AR mirror of the vendor
 * bill Edit History). Lists each approval-gated charge change with its status and
 * supporting document. `refreshKey` forces a refetch after a new change is raised.
 */
function InvoiceEditHistoryV2({ invoiceId, refreshKey }) {
	const dispatch = useDispatch();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);

	const fetchHistory = useCallback(() => {
		if (!invoiceId) return;
		setLoading(true);
		dispatch(getClientInvoiceFieldChangesV2({ client_invoice_id: invoiceId }))
			.then((res) => setRows(res?.rows || []))
			.finally(() => setLoading(false));
	}, [dispatch, invoiceId]);

	useEffect(() => {
		fetchHistory();
	}, [fetchHistory, refreshKey]);

	const openDoc = async (key, urlType) => {
		if (!key) return;
		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: urlType,
					data: { entity: CLIENT_INVOICE_ENTITY, keys: [key] },
				})
			);
			const url = res?.data?.[0]?.url;
			if (url) window.open(url);
			else message.info('Document not available.');
		} catch (e) {
			message.error('Failed to open document.');
		}
	};

	const columns = [
		{ title: 'Edited Field', dataIndex: 'field', key: 'field', render: (t) => t || '-' },
		{
			title: 'Initial Value',
			dataIndex: 'initial_value',
			key: 'initial_value',
			render: (t) => formatCurrency(t),
		},
		{
			title: 'Updated Value',
			dataIndex: 'updated_value',
			key: 'updated_value',
			render: (t) => formatCurrency(t),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (s) => (
				<span style={{ color: STATUS_COLOR[s] || '#6b7280', fontWeight: 600 }}>
					{s ? s.charAt(0).toUpperCase() + s.slice(1) : '-'}
				</span>
			),
		},
		{
			title: 'Updated By',
			dataIndex: 'updated_by',
			key: 'updated_by',
			render: (t) => t || '-',
		},
		{
			title: 'Updated At',
			dataIndex: 'updated_at',
			key: 'updated_at',
			render: (t) => formatDateTime(t),
		},
		{
			title: 'Docs',
			key: 'docs',
			render: (_v, record) =>
				record.image_key ? (
					<div style={{ display: 'flex', gap: 14 }}>
						<Tooltip title="View">
							<EyeOutlined
								style={{ color: '#2563eb', cursor: 'pointer' }}
								onClick={() => openDoc(record.image_key, 'view')}
							/>
						</Tooltip>
						<Tooltip title="Download">
							<DownloadOutlined
								style={{ color: '#475569', cursor: 'pointer' }}
								onClick={() => openDoc(record.image_key, 'download')}
							/>
						</Tooltip>
					</div>
				) : (
					'-'
				),
		},
	];

	if (!loading && rows.length === 0) return null;

	return (
		<div style={{ marginTop: 20 }}>
			<div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
				Edit History ({rows.length} CHANGE{rows.length === 1 ? '' : 'S'})
			</div>
			<Table
				rowKey={(r, i) => `${r.request_id}-${r.field_key}-${i}`}
				columns={columns}
				dataSource={rows}
				loading={loading}
				pagination={false}
				size="small"
			/>
		</div>
	);
}

InvoiceEditHistoryV2.propTypes = {
	invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	refreshKey: PropTypes.number,
};

export default InvoiceEditHistoryV2;
